import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  ArrowLeft, Cloud, CloudUpload,
  Trash2, FileDown, Check, Loader2, Moon, Sun, FileText, FileSpreadsheet, LogOut
} from 'lucide-react';
import { useTheme } from '../hooks/useTheme';
import * as XLSX from 'xlsx';
import {
  initGoogleDrive,
  hasGoogleAccess,
  backupToGoogleDrive,
  checkGoogleDriveBackup,
  startGoogleRedirectAuth,
  handleGoogleRedirectCallback
} from '../services/cloudBackup';
import { clearAllData, exportAllData } from '../services/storage';
import { saveOrShareFile } from '../lib/fileUtils';

export function BackupSettings() {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  
  // Google Drive state
  const [googleReady, setGoogleReady] = useState(false);
  const [googleConnected, setGoogleConnected] = useState(false);
  const [googleBackupInfo, setGoogleBackupInfo] = useState(null);
  const [googleLoading, setGoogleLoading] = useState(false);
  
  // Export state
  const [exportFormat, setExportFormat] = useState('json');
  const [exporting, setExporting] = useState(false);
  const [exportingTo, setExportingTo] = useState(null); // 'device' | 'drive'
  
  // Dialog state
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);

  useEffect(() => {
    async function init() {
      const redirectResult = handleGoogleRedirectCallback();
      if (redirectResult) {
        if (redirectResult.success) {
          toast.success('Connected to Google Drive!');
        } else if (redirectResult.error) {
          console.error('Google Drive OAuth redirect error:', redirectResult.error);
          toast.error('Google Drive connection failed. Please try again.');
        }
      }
      
      const googleOk = await initGoogleDrive();
      setGoogleReady(googleOk);
      setGoogleConnected(hasGoogleAccess());
      
      if (hasGoogleAccess()) {
        const info = await checkGoogleDriveBackup();
        setGoogleBackupInfo(info);
      }
    }
    init();
  }, []);

  const handleConnectGoogle = () => {
    toast.info('Redirecting to Google sign-in...');
    startGoogleRedirectAuth();
  };

  const handleDisconnectGoogle = () => {
    localStorage.removeItem('google_access_token');
    localStorage.removeItem('google_token_expiry');
    setGoogleConnected(false);
    setGoogleBackupInfo(null);
    setShowDisconnectConfirm(false);
    toast.success('Disconnected from Google Drive');
  };

  const handleExport = async (destination) => {
    setExporting(true);
    setExportingTo(destination);
    try {
      const allData = await exportAllData();
      const timestamp = new Date().toISOString().split('T')[0];

      if (destination === 'drive') {
        const result = await backupToGoogleDrive();
        const info = await checkGoogleDriveBackup();
        setGoogleBackupInfo(info);
        toast.success(result.message);
        return;
      }

      if (exportFormat === 'json') {
        const blob = new Blob([JSON.stringify(allData, null, 2)], { type: 'application/json' });
        await saveOrShareFile(blob, `minddeck-backup-${timestamp}.json`, 'application/json');
      } else if (exportFormat === 'csv') {
        const allCards = [];
        for (const ws of allData.workspaces || []) {
          for (const card of ws.cards || []) {
            allCards.push({
              workspace: ws.name,
              title: card.title,
              notes: card.body || '',
              ...Object.fromEntries(
                Object.entries(card.fields || {}).map(([k, v]) => [k, typeof v === 'object' ? v.value : v])
              )
            });
          }
        }
        if (allCards.length === 0) { toast.error('No cards to export'); return; }

        const allFields = new Set();
        allCards.forEach(c => Object.keys(c).forEach(k => allFields.add(k)));
        const headers = ['workspace', 'title', 'notes', ...Array.from(allFields).filter(h => !['workspace', 'title', 'notes'].includes(h))];
        const csvRows = [headers.join(',')];
        for (const card of allCards) {
          const row = headers.map(h => {
            const val = card[h] || '';
            return (val.includes(',') || val.includes('"') || val.includes('\n'))
              ? `"${val.replace(/"/g, '""')}"` : val;
          });
          csvRows.push(row.join(','));
        }
        const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
        await saveOrShareFile(blob, `minddeck-export-${timestamp}.csv`, 'text/csv');
      } else if (exportFormat === 'xlsx') {
        const allCards = [];
        for (const ws of allData.workspaces || []) {
          for (const card of ws.cards || []) {
            allCards.push({
              Workspace: ws.name,
              Title: card.title,
              Notes: card.body || '',
              ...Object.fromEntries(
                Object.entries(card.fields || {}).map(([k, v]) => [k, typeof v === 'object' ? v.value : v])
              )
            });
          }
        }
        if (allCards.length === 0) { toast.error('No cards to export'); return; }

        const worksheet = XLSX.utils.json_to_sheet(allCards);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Cards');
        const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        await saveOrShareFile(blob, `minddeck-export-${timestamp}.xlsx`, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
      }

      toast.success('Export complete');
    } catch (error) {
      toast.error('Export failed: ' + error.message);
    } finally {
      setExporting(false);
      setExportingTo(null);
    }
  };

  const handleClearData = async () => {
    try {
      await clearAllData();
      toast.success('All data cleared');
      setShowClearConfirm(false);
      window.location.reload();
    } catch (error) {
      toast.error('Failed to clear data');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    return new Date(dateStr).toLocaleString();
  };

  const isExporting = (dest) => exporting && exportingTo === dest;

  return (
    <div className="min-h-screen bg-background" data-testid="backup-settings">
      {/* Header */}
      <header className="border-b bg-card sticky top-0 z-50">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => navigate('/')} className="gap-2">
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
              <h1 className="text-xl font-bold">Backup & Storage</h1>
            </div>
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle">
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 max-w-2xl space-y-6">
        {/* Info Banner */}
        <Card className="bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800">
          <CardContent className="pt-6">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              <strong>Your data is stored locally</strong> on this device. Export a copy to Google Drive or download it to keep a backup.
            </p>
          </CardContent>
        </Card>

        {/* Export Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded-lg">
                <FileDown className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              </div>
              <div>
                <CardTitle className="text-lg">Export Data</CardTitle>
                <CardDescription>Save a copy of all your workspaces, cards, and decks</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Format picker */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Format</Label>
              <div className="grid grid-cols-3 gap-2">
                <button
                  onClick={() => setExportFormat('xlsx')}
                  className={`flex flex-col items-center gap-1 rounded-lg border p-3 text-center transition-colors ${
                    exportFormat === 'xlsx' ? 'border-primary bg-primary/5' : 'hover:border-primary/50'
                  }`}
                >
                  <FileSpreadsheet className="h-5 w-5 text-green-600" />
                  <span className="text-xs font-medium">Spreadsheet</span>
                  <span className="text-xs text-muted-foreground">.xlsx</span>
                </button>
                <button
                  onClick={() => setExportFormat('csv')}
                  className={`flex flex-col items-center gap-1 rounded-lg border p-3 text-center transition-colors ${
                    exportFormat === 'csv' ? 'border-primary bg-primary/5' : 'hover:border-primary/50'
                  }`}
                >
                  <FileText className="h-5 w-5 text-blue-600" />
                  <span className="text-xs font-medium">CSV</span>
                  <span className="text-xs text-muted-foreground">.csv</span>
                </button>
                <button
                  onClick={() => setExportFormat('json')}
                  className={`flex flex-col items-center gap-1 rounded-lg border p-3 text-center transition-colors ${
                    exportFormat === 'json' ? 'border-primary bg-primary/5' : 'hover:border-primary/50'
                  }`}
                >
                  <FileDown className="h-5 w-5 text-purple-600" />
                  <span className="text-xs font-medium">Full Backup</span>
                  <span className="text-xs text-muted-foreground">.json</span>
                </button>
              </div>
              <p className="text-xs text-muted-foreground">
                {exportFormat === 'json' && 'Recommended for restoring — includes all data'}
                {exportFormat === 'csv' && 'Universal spreadsheet format'}
                {exportFormat === 'xlsx' && 'Easy to view and edit in Excel or Sheets'}
              </p>
            </div>

            {/* Export destinations */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Save to</Label>
              <div className="flex flex-col gap-2">
                <Button
                  className="w-full justify-start"
                  onClick={() => handleExport('device')}
                  disabled={exporting}
                >
                  {isExporting('device')
                    ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    : <FileDown className="h-4 w-4 mr-2" />}
                  Save to Device
                </Button>

                {googleReady && !googleConnected && (
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={handleConnectGoogle}
                    disabled={exporting}
                  >
                    <Cloud className="h-4 w-4 mr-2 text-blue-500" />
                    Connect Google Drive to export there
                  </Button>
                )}

                {googleReady && googleConnected && (
                  <div className="space-y-1">
                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      onClick={() => handleExport('drive')}
                      disabled={exporting}
                    >
                      {isExporting('drive')
                        ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        : <CloudUpload className="h-4 w-4 mr-2 text-blue-500" />}
                      Save to Google Drive
                      <Badge variant="outline" className="ml-auto gap-1 text-xs">
                        <Check className="h-3 w-3" /> Connected
                      </Badge>
                    </Button>
                    {googleBackupInfo?.exists && (
                      <p className="text-xs text-muted-foreground pl-1">
                        Last saved: {formatDate(googleBackupInfo.lastModified)}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            <p className="text-xs text-muted-foreground">
              To restore from a backup, go to{' '}
              <Link to="/import-help" className="underline hover:text-foreground">Import</Link>.
            </p>
          </CardContent>
        </Card>

        {/* Disconnect link */}
        {googleConnected && (
          <div className="flex justify-end">
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground"
              onClick={() => setShowDisconnectConfirm(true)}
            >
              <LogOut className="h-3 w-3 mr-1" />
              Disconnect Google Drive
            </Button>
          </div>
        )}

        {/* Danger Zone */}
        <Card className="border-destructive/50">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                <Trash2 className="h-5 w-5 text-destructive" />
              </div>
              <div>
                <CardTitle className="text-lg text-destructive">Danger Zone</CardTitle>
                <CardDescription>Permanently delete all local data</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Button variant="destructive" onClick={() => setShowClearConfirm(true)}>
              <Trash2 className="h-4 w-4 mr-2" />
              Clear All Data
            </Button>
          </CardContent>
        </Card>
      </main>

      {/* Disconnect Confirmation */}
      <AlertDialog open={showDisconnectConfirm} onOpenChange={setShowDisconnectConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect Google Drive?</AlertDialogTitle>
            <AlertDialogDescription>
              Your Google Drive backup will remain intact. You can reconnect at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDisconnectGoogle}>
              Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Clear Data Confirmation */}
      <AlertDialog open={showClearConfirm} onOpenChange={setShowClearConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-destructive" />
              Clear All Data?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete all your workspaces, cards, and decks from this device. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleClearData} className="bg-destructive hover:bg-destructive/90">
              Yes, Delete Everything
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default BackupSettings;
